import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';
import MuxPlayer from '@mux/mux-player-react';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

// Thumbnail for a Mux video: a short animated preview served from Mux's image
// API. The thumbnail is rendered into an ~88px slot, so we request a small
// 176px-wide (2x retina) clip at a reduced frame rate to keep the download
// light on low-end/mobile devices instead of Mux's 320px default. We use
// `animated.webp` (smaller/better quality than gif) and start 1s in so the
// preview skips any black/intro frame at 0s.
const MUX_THUMB_PREVIEW_PARAMS = 'width=176&fps=10&start=1';
const muxAnimatedPreviewUrl = playbackId =>
  `https://image.mux.com/${playbackId}/animated.webp?${MUX_THUMB_PREVIEW_PARAMS}`;

const VideoThumbnail = ({ playbackId, className }) => {
  return (
    <div className={className}>
      <img
        src={muxAnimatedPreviewUrl(playbackId)}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
};

// Main gallery video player. Registers the Mux player element with the shared
// `videoRefs` map so the gallery can pause it when the slide changes, and
// shows a fallback message instead of a broken element if loading fails.
const GalleryVideo = ({ playbackId, title, className, index, videoRefs, errorLabel }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <div className={css.videoError}>{errorLabel}</div>;
  }

  return (
    <MuxPlayer
      className={className}
      streamType="on-demand"
      playbackId={playbackId}
      // title={title}
      playsInline
      // Registered so the gallery can pause this player on slide change /
      // fullscreen exit; MuxPlayer forwards the ref to the underlying element
      // which exposes `.pause()` and `.paused`, just like a <video>.
      ref={el => {
        const map = videoRefs.current;
        if (el) {
          map.set(index, el);
        } else {
          map.delete(index);
        }
      }}
      onError={() => setHasError(true)}
    />
  );
};

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  lazyLoad: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Map of slide index -> main <video> element, used to pause the previous
  // video when navigating between slides or exiting fullscreen.
  const videoRefs = useRef(new Map());
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, videos } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
    };
  });

  const videoItems = (videos || [])
    .map((video, i) => {
      const playbackId = video?.playbackId;
      if (!playbackId) {
        return null;
      }
      return {
        original: '',
        alt: intl.formatMessage(
          { id: 'ListingImageGallery.videoAltText' },
          { index: i + 1, count: videos.length }
        ),
        thumbAlt: intl.formatMessage(
          { id: 'ListingImageGallery.videoThumbnailAltText' },
          { index: i + 1, count: (videos || []).length }
        ),
        // react-image-gallery only renders a thumbnail slot (and thus calls
        // renderThumbInner) when the item has a truthy `thumbnail`, so point it
        // at Mux's animated preview. The actual <img> is rendered by
        // VideoThumbnail via renderThumbInner; this value just needs to be
        // truthy, but we keep the same small URL for consistency.
        thumbnail: muxAnimatedPreviewUrl(playbackId),
        playbackId,
        title: video?.name,
        isVideo: true,
        // Index within `allItems`, used as the key when registering the video
        // element so it can be paused on slide change.
        galleryIndex: images.length + i,
      };
    })
    .filter(Boolean);

  const allItems = [...items, ...(videoItems || [])];

  const pauseAllVideos = () => {
    videoRefs.current.forEach(video => {
      if (video && !video.paused) {
        video.pause();
      }
    });
  };

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    if (item?.isVideo) {
      return (
        <AspectRatioWrapper
          width={aspectWidth || 1}
          height={aspectHeight || 1}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <div className={css.itemCentering}>
            <GalleryVideo
              className={css.video}
              playbackId={item.playbackId}
              title={item.title}
              index={item.galleryIndex}
              videoRefs={videoRefs}
              errorLabel={intl.formatMessage({ id: 'ListingImageGallery.videoLoadError' })}
            />
          </div>
        </AspectRatioWrapper>
      );
    }

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    if (item?.isVideo) {
      return <VideoThumbnail playbackId={item.playbackId} className={css.thumb} />;
    }

    return (
      <div className={css.thumbWrapper}>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    // Entering or leaving fullscreen remounts the slides, so stop any playing
    // video to avoid audio continuing from a now-hidden element.
    pauseAllVideos();
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <div className={css.navLeft}>
        <button className={css.navArrowWrapper} disabled={disabled} onClick={onClick}>
          <IconArrowHead direction="left" size="big" />
        </button>
      </div>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <div className={css.navRight}>
        <button className={css.navArrowWrapper} disabled={disabled} onClick={onClick}>
          <IconArrowHead direction="right" size="big" />
        </button>
      </div>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: allItems.length }}
        />
      </button>
    );
  };

  if (allItems.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={allItems}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      onBeforeSlide={pauseAllVideos}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
